/**
 * Why a project embedded on someone's website (the iframe API) cannot be
 * shown. The visitor is told on /embed/unavailable, in words, instead of the
 * bare 403 and 404 pages they used to get.
 */
export type EmbedUnavailableReason =
  /** Shared as Private: only its owner and members can open it. */
  'private'
  /** Deleted, or a wrong project id in the embed. */
  | 'not_found'
  /** The key's website restrictions do not include the embedding page. */
  | 'site'
  /** The key or token is unknown, expired, revoked or replaced. */
  | 'key'
  /** The key's account cannot embed just now: plan ended, or key suspended by staff. */
  | 'account'
  /**
   * The key's plan has no embedded maps: they are Enterprise only. An Open
   * Guest key opens its own guest projects and nothing else.
   */
  | 'plan'
  /** The server could not be reached, or failed. */
  | 'error'
  /** Refused, without knowing whether it is private or gone. */
  | 'unavailable';

const REASONS: EmbedUnavailableReason[] = ['private', 'not_found', 'site', 'key', 'account', 'plan', 'error', 'unavailable'];

/** What POST /developer/project/:id/access answered. */
export interface EmbedAccess {
  granted: boolean;
  /** The server's error code when it refused ('' when it could not be reached). */
  errorCode: string;
  /**
   * A private project shown because its owner authorized the key's developer
   * (Authorized developers), and whether this session got the pass that
   * reads it.
   */
  authorized?: boolean;
  pass?: boolean;
  /**
   * Refused as another account's private project: who runs the embed (its
   * key's account), which the project's owner may authorize in one click.
   */
  developer?: EmbedDeveloper;
}

/** An embed's developer, as the server names them to a project's owner: never by email address. */
export interface EmbedDeveloper {
  uid: string;
  /** Their organisation or company, else their website, else their name; '' when they have none. */
  name: string;
}

/** The developer the server named with a refusal (`{msg: {developer}}`), when it looks like one. */
export function embedDeveloperOf(err: any): EmbedDeveloper | null {
  const d = err && err.msg && err.msg.developer;
  const uid = d && typeof d.uid === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(d.uid) ? d.uid : '';
  const name = d && typeof d.name === 'string' ? d.name.trim().slice(0, 80) : '';
  return uid ? {uid, name} : null;
}

/**
 * Where a private project's owner authorizes the embed's developer: their
 * Authorized developers, which asks them to confirm. The project goes with
 * it, so the server can check the account signed in there is its owner.
 */
export function embedAuthorizeUrl(origin: string, developerUid: string, projectId: string): string {
  return `${origin}/dashboard/profile?section=developers&authorize=${encodeURIComponent(developerUid)}`
    + `&project=${encodeURIComponent(projectId)}`;
}

/**
 * The page that tells the visitor why. A private project's id goes with it:
 * the page offers to open the project in a new tab, where its owner can
 * change who may see it.
 */
export function embedUnavailableUrl(reason: EmbedUnavailableReason, projectId: string = ''): string {
  const pid = reason === 'private' ? embedProjectIdOf(projectId) : '';
  return `/embed/unavailable?reason=${reason}` + (pid ? `&pid=${pid}` : '');
}

/** A project id from the page's address, or '' for anything that does not look like one. */
export function embedProjectIdOf(value: string | null | undefined): string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,100}$/.test(value) ? value : '';
}

/** The reason in the page's address; anything else reads as the general one. */
export function embedReasonOf(value: string | null | undefined): EmbedUnavailableReason {
  return REASONS.includes(value as EmbedUnavailableReason) ? value as EmbedUnavailableReason : 'unavailable';
}

/**
 * The reason behind the server's refusal of an embed.
 *
 * `project_unauthorized_access` reads as 'site' here, but the server sends it
 * for two things: a website the key does not allow, and another account's
 * private project. The caller tells them apart by reading the project, which
 * anyone may do once it is shared (CanvasAuthGuardService.embedRefusal).
 */
export function embedRefusalReason(errorCode: string): EmbedUnavailableReason {
  switch (errorCode) {
    case 'project_not_found':
      return 'not_found';
    case 'project_unauthorized_access':
      return 'site';
    // An unknown, expired or rotated-out key; an expired or unknown token
    // (authentication_failed); a key revoked since the token was made.
    case 'invalid_api_key':
    case 'authentication_failed':
    case 'unauthorized_action':
      return 'key';
    case 'api_key_suspended':
    case 'invalid_subscription':
      return 'account';
    case 'embed_needs_enterprise':
      return 'plan';
    default:
      return 'error';
  }
}
